import type { AxiosError } from 'axios';
import type { TBrokerProfileUpdateBody } from 'src/types/broker-profile';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  useGetBrokerProfile,
  usePutBrokerProfile,
} from 'src/api/broker-profile';
import { BrokerProfileUpdateBodySchema } from 'src/schema/broker-profile-schema';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
};

export default function BrokerProfileEditDialog({
  open,
  onClose,
  onSuccess,
}: Props) {
  const queryClient = useQueryClient();
  const {
    data: profileRes,
    isLoading,
    error,
  } = useGetBrokerProfile({
    enabled: open,
  });
  const { mutateAsync: putProfile, isPending } = usePutBrokerProfile();

  const profile = profileRes?.data;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TBrokerProfileUpdateBody>({
    mode: 'onChange',
    defaultValues: {
      firstname: '',
      lastname: '',
      title: '',
    },
    resolver: zodResolver(BrokerProfileUpdateBodySchema),
  });

  useEffect(() => {
    if (profile && open) {
      reset({
        firstname: profile.firstname,
        lastname: profile.lastname,
        title: profile.title,
      });
    }
  }, [profile, open, reset]);

  const onSubmit = handleSubmit(async (body: TBrokerProfileUpdateBody) => {
    try {
      const res = await putProfile({
        ...body,
        offer_collection_delay_minutes:
          profile?.offer_collection_delay_minutes ?? null,
        timezone: profile?.time_zone ?? null,
      });
      toast.success(res.message ?? 'Broker profile updated.');
      await queryClient.invalidateQueries({ queryKey: ['broker-profile'] });
      await queryClient.invalidateQueries({ queryKey: ['user-info'] });
      onSuccess();
    } catch (e: unknown) {
      const err = e as AxiosError<{ message?: string }>;
      toast.error(
        err.response?.data?.message ?? err.message ?? 'Update failed.',
      );
    }
  });

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>Edit broker profile</DialogTitle>
      <Box component="form" noValidate onSubmit={onSubmit}>
        <DialogContent dividers>
          {error ? (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {error instanceof Error ? error.message : String(error)}
            </Typography>
          ) : null}
          {isLoading && !profile ? (
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          ) : (
            <Stack spacing={2.5} sx={{ pt: 0.5 }}>
              <TextField
                fullWidth
                autoFocus
                label="First name"
                error={!!errors.firstname}
                helperText={errors.firstname?.message}
                {...register('firstname')}
              />
              <TextField
                fullWidth
                label="Last name"
                error={!!errors.lastname}
                helperText={errors.lastname?.message}
                {...register('lastname')}
              />
              <TextField
                fullWidth
                label="Title"
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isPending || isLoading || !!error}
          >
            {isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
